import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useLang } from "../context/LanguageContext";
import { Check, AlertCircle } from "lucide-react";
import SuccessModal from "./SuccessModal";
import { getAttribution, trackRegistrationStart } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * LeadForm — used on /huseier, /pendler, and /kontakt
 * props:
 *  - role: "huseier" | "pendler" | "contact"
 *  - locationLabel: label key — "station" | "address" | null
 *  - showMessage: bool — show optional message textarea
 *  - source: optional source tag (page name)
 *  - testIdPrefix: prefix for data-testid
 *  - successOverride: alternative success message
 */
export default function LeadForm({
  role,
  locationLabel = null,
  showMessage = false,
  source,
  testIdPrefix = "lead",
  successOverride,
  submitLabel,
  microcopy,
}) {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", contact: "", location: "", message: "" });
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalVariant, setModalVariant] = useState(null); // pendler | huseier | both

  const startedRef = React.useRef(false);

  const onChange = (e) => {
    if (!startedRef.current && (role === "huseier" || role === "pendler")) {
      startedRef.current = true;
      trackRegistrationStart(role);
    }
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.contact.trim()) return;
    if (!consent) {
      setError(t.common.consentRequired);
      return;
    }
    setStatus("loading");
    setError(null);
    try {
      const { data } = await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        contact: form.contact.trim(),
        role,
        location: form.location.trim() || null,
        message: form.message.trim() || null,
        source: source || role,
        consent: true,
        language: lang,
        attribution: getAttribution(),
      });
      setStatus("success");
      // Modal wording follows the role(s) this visitor actually registered.
      if (role === "huseier" || role === "pendler") {
        setModalVariant(data?.is_both === true ? "both" : role);
        setModalOpen(true);
      }
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.detail || t.common.errorGeneric);
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <>
        <div
          data-testid={`${testIdPrefix}-form-success`}
          className="relative bg-amber-deep border border-amber p-8 md:p-10 l-bracket-tl l-bracket-tr l-bracket-bl l-bracket-br"
        >
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-amber flex items-center justify-center shrink-0">
              <Check className="text-ink" size={22} strokeWidth={3} />
            </div>
            <div>
              <h3 className="font-display text-2xl md:text-3xl font-bold text-cream mb-2 tracking-tight">
                {t.common.successTitle}
              </h3>
              <p className="text-cream/70 text-sm md:text-base leading-relaxed">
                {successOverride || t.common.successBody}
              </p>
            </div>
          </div>
        </div>
        {modalVariant && (
          <SuccessModal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            variant={modalVariant}
            contact={form.contact}
          />
        )}
      </>
    );
  }

  const fieldClass =
    "w-full bg-transparent border-b border-cream/20 focus:border-amber py-3 text-base md:text-lg text-cream placeholder:text-cream/30 transition-colors";

  return (
    <form
      onSubmit={onSubmit}
      data-testid={`${testIdPrefix}-form`}
      className="relative bg-ink/40 border border-amber/30 p-7 md:p-10 l-bracket-tl l-bracket-tr l-bracket-bl l-bracket-br"
    >
      <div className="space-y-7">
        <div>
          <label className="block text-xs font-bold tracking-widest uppercase text-amber/80 mb-2">
            Navn / Name
          </label>
          <input
            type="text"
            name="name"
            required
            value={form.name}
            onChange={onChange}
            placeholder={t.common.namePlaceholder}
            data-testid={`${testIdPrefix}-input-name`}
            className={fieldClass}
          />
        </div>

        <div>
          <label className="block text-xs font-bold tracking-widest uppercase text-amber/80 mb-2">
            E-post / Telefon
          </label>
          <input
            type="text"
            name="contact"
            required
            value={form.contact}
            onChange={onChange}
            placeholder={t.common.contactPlaceholder}
            data-testid={`${testIdPrefix}-input-contact`}
            className={fieldClass}
          />
        </div>

        {locationLabel && (
          <div>
            <label className="block text-xs font-bold tracking-widest uppercase text-amber/80 mb-2">
              {locationLabel === "station" ? "Stasjon / Station" : "Adresse / Address"}
            </label>
            <input
              type="text"
              name="location"
              value={form.location}
              onChange={onChange}
              placeholder={
                locationLabel === "station" ? t.common.stationPlaceholder : t.common.addressPlaceholder
              }
              data-testid={`${testIdPrefix}-input-location`}
              className={fieldClass}
            />
          </div>
        )}

        {showMessage && (
          <div>
            <label className="block text-xs font-bold tracking-widest uppercase text-amber/80 mb-2">
              Melding / Message
            </label>
            <textarea
              name="message"
              rows={4}
              value={form.message}
              onChange={onChange}
              placeholder={t.common.messagePlaceholder}
              data-testid={`${testIdPrefix}-input-message`}
              className={`${fieldClass} resize-none`}
            />
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 text-orange text-sm" data-testid={`${testIdPrefix}-form-error`}>
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <label
          className="flex items-start gap-3 cursor-pointer group select-none"
          data-testid={`${testIdPrefix}-consent-wrapper`}
        >
          <span className="relative mt-0.5 shrink-0">
            <input
              type="checkbox"
              required
              checked={consent}
              onChange={(e) => {
                setConsent(e.target.checked);
                if (e.target.checked && error === t.common.consentRequired) setError(null);
              }}
              data-testid={`${testIdPrefix}-consent-checkbox`}
              className="peer appearance-none w-5 h-5 border-2 border-amber/50 bg-transparent checked:bg-amber checked:border-amber transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber/40"
            />
            <Check
              size={14}
              strokeWidth={3}
              className="absolute top-0.5 left-0.5 text-ink opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none"
            />
          </span>
          <span className="text-xs md:text-sm text-cream/70 leading-relaxed">
            {t.common.consentLabel}{" "}
            <Link
              to="/personvern"
              target="_blank"
              rel="noopener noreferrer"
              className="amber-link text-amber"
              data-testid={`${testIdPrefix}-consent-link`}
              onClick={(e) => e.stopPropagation()}
            >
              {t.common.consentLinkText}
            </Link>
            .
          </span>
        </label>

        <button
          type="submit"
          disabled={status === "loading"}
          data-testid={`${testIdPrefix}-form-submit`}
          className="w-full h-14 bg-amber text-ink font-display font-bold text-base md:text-lg tracking-tight hover:bg-amber-hover transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
        >
          {status === "loading" ? t.common.submitting : (submitLabel || t.common.submit)}
          {status !== "loading" && <span className="text-xl leading-none">→</span>}
        </button>

        {microcopy && (
          <p className="text-sm text-cream/60 leading-relaxed text-center -mt-2" data-testid={`${testIdPrefix}-form-microcopy`}>
            {microcopy}
          </p>
        )}

        <p className="text-xs text-cream/40 leading-relaxed" data-testid={`${testIdPrefix}-form-privacy`}>
          {t.common.privacy}
        </p>
      </div>
    </form>
  );
}
